// React Dependencies
import React, { Component } from 'react';
import { connect } from 'react-redux';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@material-ui/core';
import _ from 'lodash';

// Internal Dependencies
import OverLoading from '../global/OverLoading';
import Constants, { Status } from '../../common/constants';
import { IsEmpty, IsString } from '../../common/checks';
import MText from '../common/MText';
import Colours from '../../styles/colours';
import MButton from '../common/MButton';
import PoiMap from '../common/poi/poiMap';
import apiService from '../../services.js/apiService';
import ContriPoiTextField from './ContriPoiTextField';
import { updateModalContent, toggleModal } from '../../actions/modal';
import { Trim, formatTag } from '../../common/util';
import Tagging from '../common/tagging';
import Images from '../basics/Images';

class ContriPoiInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading:       false,
      redirectToPoi: false,
      poiId:         null,

      // Process states
      processRemarks: props.contriPoi.processRemarks || '',
      selectedStatus: props.contriPoi.status || Constants.Status.Pending,
    };

    this.handleProcessContriPOI = this.handleProcessContriPOI.bind(this);
    this.handleStatusChange = this.handleStatusChange.bind(this);
    this.openPhotoModal = this.openPhotoModal.bind(this);
    this.confirmProcessContriPOI = this.confirmProcessContriPOI.bind(this);
  }

  handleProcessRemarksChange = (e) => {
    this.setState({ processRemarks: e.target.value });
  };

  handleStatusChange = (e) => {
    this.setState({ selectedStatus: e.target.value });
  };

  async handleProcessContriPOI() {
    try {
      const { selectedStatus, processRemarks } = this.state;

      // Validate process remarks for rejection
      if (selectedStatus === Constants.Status.Rejected && IsEmpty(Trim(processRemarks))) {
        toast.error('Please provide process remarks for rejection');
        return;
      }

      if (selectedStatus === Constants.Status.Pending) {
        toast.warn('Please select Approved or Rejected status');
        return;
      }

      const actionText = selectedStatus === Constants.Status.Approved ? 'approve' : 'reject';

      this.props.updateModalContent({
        title: (
          <MText
            text="Confirm Action"
            size={18}
            bold
            colour={Colours.Gray6}
          />
        ),
        content: (
          <div className="p-3">
            <MText colour={Colours.Gray6} style={{ display: 'inline' }}>
              Are you sure you want to
              {' '}
              <MText
                text={actionText}
                bold
                colour={Colours.Gray6}
                style={{ display: 'inline' }}
              />
              {' '}
              this contribution?
            </MText>
          </div>
        ),
        actionButton: {
          actionText: 'Confirm',
          actionType: 'primary',
          action:     this.confirmProcessContriPOI,
        },
        cancelText:       'Cancel',
        centered:         true,
        closeAfterAction: true,
      });

      this.props.toggleModal();
    } catch (err) {
      this.setState({ loading: false });
      toast.error(IsString(err) ? err : err.message);
    }
  }

  async confirmProcessContriPOI() {
    try {
      const { selectedStatus, processRemarks } = this.state;
      const { contriPoi, onRefresh } = this.props;

      this.setState({ loading: true });

      if (selectedStatus === Constants.Status.Approved) {
        await this.createPoiFromContriPOI();
      } else if (selectedStatus === Constants.Status.Rejected) {
        const payload = {
          status: Constants.Status.Rejected,
          processRemarks,
        };

        await apiService.updateContriPOI(contriPoi.id, payload);
        toast.success('Contributed POI has been rejected');

        await onRefresh();
        this.setState({ loading: false });
      }
    } catch (err) {
      this.setState({ loading: false });
      toast.error(IsString(err) ? err : err.message);
    }
  }

  async createPoiFromContriPOI() {
    const { processRemarks } = this.state;
    const contriPoiId = this.props.contriPoi.id;

    // Update contriPOI with the created POI ID
    const contriPoiPayload = {
      status: Constants.Status.Approved,
      processRemarks,
    };

    const updatedContriPoi = await apiService.updateContriPOI(contriPoiId, contriPoiPayload);

    toast.success('POI created successfully and contribution approved');
    this.setState({
      poiId:         updatedContriPoi.poiId,
      redirectToPoi: true,
    });
  }

  openPhotoModal(photo, index) {
    const { photos } = this.props.contriPoi;
    if (IsEmpty(photo) || IsEmpty(photos)) return;

    const totalPhotos = photos.length;

    const sectionStyle = {
      width:        '100%',
      marginBottom: '12px',
      borderRadius: '8px',
      overflow:     'hidden',
    };

    const innerContentStyle = {
      width:  '90%',
      margin: '0 auto',
    };

    const imageWrapperStyle = {
      position:       'relative',
      width:          '100%',
      maxHeight:      '70vh',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      borderRadius:   '8px',
      overflow:       'hidden',
    };

    this.props.updateModalContent({
      title: (
        <div className="d-flex align-items-center justify-content-between w-100">
          <MText text={`Photo ${index + 1} of ${totalPhotos}`} size={16} bold colour={Colours.Gray6} />
        </div>
      ),
      content: (
        <div style={{ maxWidth: '100%', overflow: 'auto' }}>
          {/* Image */}
          <div style={imageWrapperStyle}>
            <img
              src={photo.url}
              alt="Full size"
              style={{
                maxWidth:  '90%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
          {/* Location */}
          {!IsEmpty(photo) && !IsEmpty(photo.location) && !IsEmpty(photo.location.lat) && !IsEmpty(photo.location.lng) && (
            <div style={sectionStyle}>
              <div style={innerContentStyle}>
                <div className="mt-3">
                  <MText text="Location" size={12} bold colour={Colours.Gray6} style={{ marginBottom: '4px' }} />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <PoiMap
                    mapHeight={250}
                    coord={{
                      lat: photo.location.lat,
                      lng: photo.location.lng,
                    }}
                    isPoiMarker
                    zoom={18}
                    markerScaledSize={20}
                    disabled
                    markerIcon={Images.Local.PoiPhotoLocationMarker}
                  />
                </div>
              </div>
            </div>
          )}
          {/* Tags*/}
          {!IsEmpty(photo) && !IsEmpty(photo.tags) && (
            <div style={sectionStyle}>
              <div style={innerContentStyle}>
                <div className="mt-3 d-flex flex-column">
                  <MText text="Tags" size={12} bold colour={Colours.Gray6} style={{ marginBottom: '10px' }} />
                  <div className="d-flex flex-row flex-wrap">
                    { _.map(photo.tags, (t) => <MText text={`#${t}`} className="mr-2" />)}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Photo Info */}
          {!IsEmpty(photo) && !IsEmpty(photo.info) && (
            <div
              style={{
                padding:         '16px',
                backgroundColor: Colours.White,
                borderRadius:    '8px',
              }}
            >
              {!IsEmpty(photo.info) && !IsEmpty(photo.info.text) && (
                <div className="mb-3">
                  <div
                    style={{
                      padding:         '12px',
                      backgroundColor: Colours.LightGray1,
                      borderRadius:    '4px',
                    }}
                  >
                    <MText text="Caption" size={12} semibold colour={Colours.Gray6} style={{ marginBottom: '8px' }} />
                    <MText text={photo.info.text} />
                  </div>
                </div>
              )}

              <div className="row">
                {!IsEmpty(photo.info) && !IsEmpty(photo.info.takenOn) && (
                  <div className="col-12 col-md-6 mb-3">
                    <div
                      style={{
                        padding:         '12px',
                        backgroundColor: Colours.LightGray1,
                        borderRadius:    '4px',
                      }}
                    >
                      <MText text="Date Taken" size={12} semibold colour={Colours.Gray6} style={{ marginBottom: '4px' }} />
                      <MText text={moment(photo.info.takenOn).format('DD MMMM YYYY')} />
                    </div>
                  </div>
                )}

                {!IsEmpty(photo.info) && !IsEmpty(photo.info.contributor) && (
                  <div className="col-12 col-md-6 mb-3">
                    <div
                      style={{
                        padding:         '12px',
                        backgroundColor: Colours.LightGray1,
                        borderRadius:    '4px',
                      }}
                    >
                      <MText text="Contributor" size={12} semibold colour={Colours.Gray6} style={{ marginBottom: '4px' }} />
                      <MText text={photo.info.contributor} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ),
      centered:         true,
      closeAfterAction: true,
      cancelText:       'Close',
    });

    this.props.toggleModal();
  }

  renderPhotoGallery(photos) {
    if (IsEmpty(photos) || !Array.isArray(photos) || photos.length === 0) {
      return null;
    }

    return (
      <div className="mt-4">
        <MText text="Photos" size={20} bold colour={Colours.Gray6} className="mb-3" />
        <div className="row">
          {photos.map((photo, index) => (
            <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3" key={photo.id || `photo-${index}`}>
              <div className="photo-card h-100">
                <div
                  role="button"
                  tabIndex={0}
                  className="position-relative w-100"
                  style={{
                    cursor:       'pointer',
                    overflow:     'hidden',
                    borderRadius: '4px',
                    outline:      'none',
                  }}
                  onClick={() => this.openPhotoModal(photo, index)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      this.openPhotoModal(photo, index);
                    }
                  }}
                >
                  <img
                    src={photo.url}
                    alt={`Contribution ${index + 1}`}
                    className="img-fluid w-100"
                    style={{
                      height:     '200px',
                      objectFit:  'cover',
                      transition: 'transform 0.3s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  />
                  <div
                    style={{
                      position:   'absolute',
                      bottom:     0,
                      left:       0,
                      right:      0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      color:      Colours.White,
                      padding:    '8px',
                      fontSize:   '12px',
                    }}
                  >
                    {!IsEmpty(photo.info) && !IsEmpty(photo.info.text) && (
                      <div className="text-truncate">
                        <MText text={photo.info.text} size={12} colour={Colours.White} />
                      </div>
                    )}
                  </div>
                </div>
                {!IsEmpty(photo.info) && (
                  <div className="mt-2">
                    {!IsEmpty(photo.info.text) && (
                      <div className="text-truncate" title={photo.info.text}>
                        <MText text="Caption: " size={12} bold colour={Colours.Gray6} />
                        <MText text={photo.info.text} size={12} colour={Colours.Gray6} />
                      </div>
                    )}
                    {!IsEmpty(photo.info.takenOn) && moment(photo.info.takenOn).isValid() && (
                      <div>
                        <MText text="Date: " size={12} bold colour={Colours.Gray6} />
                        <MText text={moment(photo.info.takenOn).format('DD MMM YYYY')} size={12} colour={Colours.Gray6} />
                      </div>
                    )}
                    {!IsEmpty(photo.info.contributor) && (
                      <div className="text-truncate" title={photo.info.contributor}>
                        <MText text="Contributor: " size={12} bold colour={Colours.Gray6} />
                        <MText text={photo.info.contributor} size={12} colour={Colours.Gray6} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    if (this.state.redirectToPoi && this.state.poiId) {
      return <Redirect to={`/admin/poi/${this.state.poiId}`} />;
    }

    const { processRemarks, loading, selectedStatus } = this.state;
    const { contriPoi } = this.props;

    if (IsEmpty(contriPoi)) {
      return (
        <div className="container-fluid mt-3">
          <OverLoading />
        </div>
      );
    }

    const photoCoords = [];
    contriPoi.photos.forEach((photo) => {
      if (
        !IsEmpty(photo?.location?.lat)
        && !IsEmpty(photo?.location?.lng)
      ) {
        photoCoords.push({
          lat: photo.location.lat,
          lng: photo.location.lng,
        });
      }
    });

    const isPending = contriPoi.status === Status.Pending;
    const isUpdateDisabled = !isPending;

    const mandatoryTags = [];

    if (!IsEmpty(contriPoi.tags)) {
      mandatoryTags.push(formatTag(contriPoi.tags));
    }

    return (
      <div className="container-fluid mt-3">
        <div className="row">
          <div className="col-12">
            <div className="position-relative">
              {loading && <OverLoading />}

              <MText
                text="Review Contributed POI"
                size={24}
                bold
                colour={Colours.Gray6}
                className="mb-4"
              />

              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <ContriPoiTextField
                    label="Name"
                    value={IsEmpty(contriPoi.name) ? '' : contriPoi.name}
                  />

                  <ContriPoiTextField
                    label="Address"
                    value={IsEmpty(contriPoi.address) ? '' : contriPoi.address}
                  />

                  <ContriPoiTextField
                    label="Postal Code"
                    value={IsEmpty(contriPoi.postalCode) ? '' : contriPoi.postalCode}
                  />

                  <ContriPoiTextField
                    label="Description"
                    value={IsEmpty(contriPoi.description) ? '' : contriPoi.description}
                  />

                  <div className="form-group">
                    <div className="form-group">
                      <MText text="Tags" />
                      <Tagging
                        textSize="h6"
                        data={[...contriPoi.tags].map((tag) => ({ name: tag, removable: mandatoryTags.includes(tag) }))}
                        disabled
                        onChange={(values) => {
                          const formattedUserTags = values.map((value) => formatTag(value));
                          this.setState((prevState) => ({
                            formData: {
                              ...prevState.formData,
                              tags: formattedUserTags,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <ContriPoiTextField label="Location">
                    <MText
                      text={`Latitude: ${IsEmpty(contriPoi.lat) ? '' : contriPoi.lat}`}
                      colour={Colours.Gray6}
                    />
                    <MText
                      text={`Longitude: ${IsEmpty(contriPoi.lng) ? '' : contriPoi.lng}`}
                      colour={Colours.Gray6}
                      className="mt-1"
                    />
                  </ContriPoiTextField>

                  <ContriPoiTextField
                    label="Opening Hours"
                    value={IsEmpty(contriPoi.hours) ? 'No opening hours provided' : contriPoi.hours}
                  />

                  <div className="form-group mt-4">
                    <MText text="Process Remarks" semibold colour={Colours.Gray6} />
                    <textarea
                      className="form-control"
                      rows="4"
                      value={processRemarks}
                      onChange={this.handleProcessRemarksChange}
                      placeholder={isPending ? 'Required for rejection, optional for approval' : ''}
                      disabled={!isPending}
                    />
                  </div>

                  <div className="form-group mt-4">
                    <MText size={20} text="Set Status" colour={Colours.Gray6} />
                    <RadioGroup
                      className="mt-2"
                      value={selectedStatus}
                      onChange={this.handleStatusChange}
                      row
                    >
                      <FormControlLabel
                        value={Constants.Status.Approved}
                        control={<Radio color="primary" />}
                        label={<MText text={Constants.Status.Approved} colour={Colours.Gray6} />}
                        disabled={!isPending}
                      />
                      <FormControlLabel
                        value={Constants.Status.Rejected}
                        control={<Radio color="primary" />}
                        label={<MText text={Constants.Status.Rejected} colour={Colours.Gray6} />}
                        disabled={!isPending}
                      />
                    </RadioGroup>
                  </div>

                  {isPending && (
                    <div className="d-flex justify-content-center mt-5 mb-2">
                      <MButton
                        text="PROCESS CONTRIPOI"
                        variant="contained"
                        colour={isUpdateDisabled ? Colours.Gray2 : Colours.Primary}
                        textColour={Colours.White}
                        onClick={this.handleProcessContriPOI}
                        disabled={loading || isUpdateDisabled}
                        style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                      />
                    </div>
                  )}
                </div>

                <div className="col-lg-6 col-sm-12">
                  <div className="mb-4">
                    <PoiMap
                      mapHeight={450}
                      coord={{
                        lat: contriPoi.lat,
                        lng: contriPoi.lng,
                      }}
                      additionalMarkers={photoCoords}
                      isPoiMarker
                      zoom={18}
                      disabled
                    />
                  </div>

                  {this.renderPhotoGallery(contriPoi.photos)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ContriPoiInfo.propTypes = {
  contriPoi:          PropTypes.shape(Object).isRequired,
  onRefresh:          PropTypes.func.isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  updateModalContent,
  toggleModal,
};

export default connect(
  null,
  mapDispatchToProps,
)(ContriPoiInfo);
